import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import {
  CountTabs,
  DetailGrid,
  DetailItem,
  PanelFooter,
  PanelTitleBlock,
  SalarySection,
  SummaryStrip,
  ToneChip,
  VersionChip,
  downloadCsv,
  useSalaryPanelTrail,
} from "../Shared/salaryUi";
import { SALARY_REVISION_PATH, employeeStatus, formatDay, formatRupees, periodOf, templateText, versionLabel } from "./employeeSalaryData";

const SCREEN = "Employee Salary";
const money = (value) => (value == null ? "—" : formatRupees(Math.round(value)));
const signed = (value) => `${value >= 0 ? "+" : "−"}${formatRupees(Math.abs(Math.round(value)))}`;
const pct = (from, to) => (from ? `${to >= from ? "+" : ""}${Math.round(((to - from) / from) * 1000) / 10}%` : "");
const initials = (name) => String(name || "?").split(" ").map((part) => part[0]).join("").slice(0, 2).toUpperCase();
const loadingText = (text) => <Mui.Typography sx={{ ...designType.helperText, py: 2 }}>{text}</Mui.Typography>;
const errorText = (text) => <RequestInfoBanner tone="danger" body={text} />;

// ---------------- Salary details ----------------

const DetailsTab = ({ employee, details, onViewRevision }) => {
  const period = periodOf(employee.pay_basis);
  const periodLabel = period === "day" ? "Daily" : period === "week" ? "Weekly" : "Monthly";
  const cellHead = { ...designShell.tableHeadCellBase, px: 2, py: 1.25, display: "flex", justifyContent: "space-between", backgroundColor: designColors.slateBg, borderBottom: `1px solid ${designColors.slate200}` };
  const line = (key, label, value, strong) => (
    <Mui.Stack key={key} direction="row" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700, fontWeight: strong ? designWeights.heading : 400 }}>{label}</Mui.Typography>
      <Mui.Typography sx={{ fontSize: 14, color: designColors.slate900, fontWeight: strong ? designWeights.heading : 400 }}>{value}</Mui.Typography>
    </Mui.Stack>
  );
  const empty = (text) => <Mui.Typography sx={{ ...designType.helperText, px: 2, py: 1 }}>{text}</Mui.Typography>;
  const { record } = details;
  return (
    <Mui.Stack spacing={2}>
      {details.pending.map((item, index) => (
        <Mui.Stack key={item.number || index} direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ backgroundColor: "#FFF7ED", border: "1px solid #F2D680", borderRadius: designRadii.card, px: 2, py: 1.25 }}>
          <Mui.Typography sx={{ fontSize: 14, color: "#A15C00" }}>
            <b>{item.kind === "Upcoming" ? "Approved, starts later:" : "Revision pending:"}</b>{" "}
            {[item.change || item.reason, item.gross != null ? `to ${money(item.gross)}` : null, item.from ? `from ${formatDay(item.from)}` : null].filter(Boolean).join(" ")}
            {item.number ? ` · ${item.number}` : ""}
            {item.kind === "Proposed" && item.status === "Pending" && item.level ? ` · waiting at L${item.level}` : ""}
          </Mui.Typography>
          {item.number ? (
            <Mui.Link component="button" type="button" underline="hover" onClick={() => onViewRevision(item.number)} sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: "#A15C00", flexShrink: 0 }}>View</Mui.Link>
          ) : null}
        </Mui.Stack>
      ))}
      <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflow: "hidden", backgroundColor: designColors.white }}>
        <Mui.Box sx={{ display: "flex", flexDirection: "column" }}>
          <Mui.Box sx={cellHead}><span>EARNINGS</span><span>{periodLabel}</span></Mui.Box>
          <Mui.Box sx={{ flex: 1, py: 0.5 }}>
            {details.earnings.length ? details.earnings.map((item, index) => line(`${item.code}-${index}`, item.name, money(item.amount))) : empty("No earnings sent by the server.")}
          </Mui.Box>
          <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}` }}>
            {line("gross", "GROSS", money(details.gross_total), true)}
            {details.additional_total ? line("additional", "Additional Earnings (on top of GROSS)", money(details.additional_total)) : null}
          </Mui.Box>
        </Mui.Box>
        <Mui.Box sx={{ display: "flex", flexDirection: "column", borderLeft: { md: `1px solid ${designColors.slate200}` }, borderTop: { xs: `1px solid ${designColors.slate200}`, md: "none" } }}>
          <Mui.Box sx={cellHead}><span>DEDUCTIONS</span><span>{periodLabel}</span></Mui.Box>
          <Mui.Box sx={{ flex: 1, py: 0.5 }}>
            {details.deductions.length ? details.deductions.map((item, index) => line(`${item.code}-${index}`, item.name, money(item.amount))) : empty("No deductions in this salary.")}
            {details.statutory ? (
              <>
                <Mui.Typography sx={{ ...designType.captionText, px: 2, pt: 1.5, pb: 0.5 }}>Statutory, calculated at payroll:</Mui.Typography>
                {details.statutory.map((hint) => (
                  <Mui.Stack key={hint.label} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 0.75 }}>
                    <Mui.Typography sx={{ fontSize: 14, color: designColors.slate700 }}>{hint.label}</Mui.Typography>
                    {hint.value ? <ToneChip label={hint.value} tone={/not|exempt/i.test(hint.value) ? "slate" : "green"} /> : null}
                  </Mui.Stack>
                ))}
              </>
            ) : null}
          </Mui.Box>
          <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}` }}>{line("deductions", "Deductions", `${money(details.deductions_total)} + statutory`, true)}</Mui.Box>
        </Mui.Box>
      </Mui.Box>
      <Mui.Stack direction="row" justifyContent="space-between" sx={{ border: "1px solid #A7E3CB", backgroundColor: "#ECFDF3", borderRadius: designRadii.card, px: 2, py: 1.5 }}>
        <Mui.Typography sx={{ fontSize: 16, fontWeight: designWeights.heading, color: "#067647" }}>NET before statutory</Mui.Typography>
        <Mui.Typography sx={{ fontSize: 16, fontWeight: designWeights.heading, color: "#067647" }}>{money(details.net)} / {period}</Mui.Typography>
      </Mui.Stack>
      {details.employer?.length || details.reimbursements?.length ? (
        <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflow: "hidden", backgroundColor: designColors.white }}>
          {details.employer?.length ? (
            <>
              <Mui.Box sx={cellHead}><span>EMPLOYER CONTRIBUTION</span><span>{periodLabel}</span></Mui.Box>
              <Mui.Box sx={{ py: 0.5 }}>{details.employer.map((item, index) => line(`employer-${item.code}-${index}`, item.name, money(item.amount)))}</Mui.Box>
              <Mui.Box sx={{ borderTop: `1px solid ${designColors.slate200}` }}>
                {details.ctc != null ? line("ctc", "CTC before statutory", money(details.ctc), true) : null}
              </Mui.Box>
            </>
          ) : null}
          {details.reimbursements?.length ? (
            <>
              <Mui.Box sx={cellHead}><span>REIMBURSEMENT (OUTSIDE SALARY)</span><span>{periodLabel}</span></Mui.Box>
              <Mui.Box sx={{ py: 0.5 }}>{details.reimbursements.map((item, index) => line(`reimb-${item.code}-${index}`, item.name, money(item.amount)))}</Mui.Box>
            </>
          ) : null}
        </Mui.Box>
      ) : null}
      <RequestInfoBanner body={`Payslip names and order come from the template. Amounts are full-${period} entitlement; payroll prorates for paid days and adds Pay Inputs and statutory amounts.`} />
      <SalarySection title="Salary record">
        <DetailGrid>
          <DetailItem label="Salary version" value={record.version ? `${versionLabel(record.version)}${record.reason ? ` · from ${record.reason}` : ""}` : record.reason || "–"} />
          <DetailItem label="Request" value={record.number ? <Mui.Box component="span" sx={{ color: designColors.tealDark, fontWeight: designWeights.semibold }}>{record.number}</Mui.Box> : "–"} />
          <DetailItem label="Approved by" value={record.approved_by || "–"} />
          <DetailItem label="First used in payroll" value={record.first_payroll || employee.first_payroll || "Not yet"} />
        </DetailGrid>
      </SalarySection>
    </Mui.Stack>
  );
};

// ---------------- Salary history ----------------

const HistoryCard = ({ checked, onToggle, tone, dot, chips, title, amount, lines, dashed }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Mui.Box sx={{ width: 18, height: 18, mt: 2.25, flexShrink: 0, borderRadius: "50%", ...dot }} />
    <Mui.Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: `${tone === "current" ? 2 : 1}px ${dashed ? "dashed" : "solid"} ${tone === "current" ? designColors.teal : tone === "proposed" ? "#F0954A" : designColors.slate200}`,
        backgroundColor: tone === "proposed" ? "#FFFBF5" : tone === "past" ? designColors.slateBg : designColors.white,
        borderRadius: designRadii.card,
        px: 2,
        py: 1.5,
      }}
    >
      <Mui.Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
          {onToggle ? <Mui.Checkbox size="small" checked={checked} onChange={onToggle} sx={{ p: 0.25, "&.Mui-checked": { color: designColors.tealDark } }} inputProps={{ "aria-label": "Select for compare" }} /> : null}
          {chips}
          <Mui.Typography sx={{ fontSize: 15, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{title}</Mui.Typography>
        </Mui.Stack>
        <Mui.Typography sx={{ fontSize: 16, fontWeight: designWeights.heading, color: designColors.slate900, flexShrink: 0 }}>{amount}</Mui.Typography>
      </Mui.Stack>
      {lines.filter(Boolean).map((text) => (
        <Mui.Typography key={text} sx={{ ...designType.helperText, mt: 0.5 }}>{text}</Mui.Typography>
      ))}
    </Mui.Box>
  </Mui.Stack>
);

// Past and current versions can be compared (they have a structure id).
const comparable = (entry) => Boolean(entry.id) && (entry.kind === "Current" || entry.kind === "Past");
const entryLabel = (entry) => (entry.version ? versionLabel(entry.version) : formatDay(entry.from));

const HistoryTab = ({ employee, entries, selected, setSelected, onCompare }) => {
  const period = periodOf(employee.pay_basis);
  const current = entries.find((entry) => entry.kind === "Current");
  const versions = entries.filter((entry) => entry.kind === "Current" || entry.kind === "Past");
  const toggle = (id) => setSelected((list) => (list.includes(id) ? list.filter((item) => item !== id) : [...list.slice(-1), id]));
  const suffix = period === "month" ? "" : ` / ${period}`;
  const templateLine = (entry) => `${templateText(entry.template_name, entry.template_version)} · ${entry.customized ? "Customized" : "Template Linked"}`;
  const titleFor = (entry, previous) => {
    const change = entry.change_pct != null ? `${entry.change_pct >= 0 ? "+" : ""}${entry.change_pct}%` : previous && entry.gross != null && previous.gross ? pct(previous.gross, entry.gross) : "";
    return [entry.reason || (entry.kind === "Proposed" ? "Revision" : "Salary"), change, entry.detail].filter(Boolean).join(" · ");
  };
  const picked = entries.filter((entry) => selected.includes(entry.id));
  return (
    <Mui.Stack spacing={1.5}>
      {picked.length === 2 ? (
        <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ border: "1px solid #A7E3CB", backgroundColor: "#ECFDF3", borderRadius: designRadii.card, px: 2, py: 1.25 }}>
          <Mui.Typography sx={{ fontSize: 14, color: "#067647" }}>
            <b>2 selected:</b> {[...picked].reverse().map(entryLabel).join(" and ")}
          </Mui.Typography>
          <PrimaryButton onClick={onCompare} sx={{ minWidth: 0, minHeight: 36, px: 2 }}>Compare versions</PrimaryButton>
        </Mui.Stack>
      ) : null}
      {entries.map((entry, index) => {
        const future = entry.kind === "Proposed" || entry.kind === "Upcoming";
        if (future) {
          return (
            <HistoryCard
              key={`${entry.kind}-${entry.id || entry.number || index}`}
              tone="proposed"
              dashed
              dot={{ border: "3px solid #F0954A", backgroundColor: "#FDBA74" }}
              chips={
                <>
                  <ToneChip label={entry.kind} tone="slate" />
                  {entry.request_status ? <ToneChip label={entry.request_status === "Approved" ? "Approved" : entry.request_status === "Pending" ? `Pending L${entry.level || 1}` : entry.request_status} tone={entry.request_status === "Approved" ? "green" : "amber"} dot /> : null}
                </>
              }
              title={titleFor(entry, current)}
              amount={`${money(entry.gross)}${suffix}`}
              lines={[templateLine(entry), [entry.from ? `From ${formatDay(entry.from)}` : null, entry.number || null, entry.request_status === "Pending" && entry.level ? `waiting at L${entry.level}` : null].filter(Boolean).join(" · ")]}
            />
          );
        }
        const isCurrent = entry.kind === "Current";
        const position = versions.indexOf(entry);
        const previous = versions[position + 1];
        return (
          <HistoryCard
            key={`${entry.kind}-${entry.id || index}`}
            tone={isCurrent ? "current" : "past"}
            checked={selected.includes(entry.id)}
            onToggle={comparable(entry) && versions.filter(comparable).length > 1 ? () => toggle(entry.id) : null}
            dot={isCurrent ? { backgroundColor: designColors.tealDark, border: `3px solid ${designColors.tealTint}` } : { border: `3px solid ${designColors.slate300}`, backgroundColor: designColors.slate100 }}
            chips={
              <>
                {entry.version ? <VersionChip label={versionLabel(entry.version)} current={isCurrent} /> : null}
                <ToneChip label={isCurrent ? "Current" : "Past"} tone={isCurrent ? "green" : "slate"} dot={isCurrent} />
              </>
            }
            title={titleFor(entry, previous)}
            amount={`${money(entry.gross)}${suffix}`}
            lines={[
              templateLine(entry),
              `${formatDay(entry.from)} → ${entry.to ? formatDay(entry.to) : "today"}${entry.approved_by ? ` · approved by ${entry.approved_by}` : ""}`,
            ]}
          />
        );
      })}
      {!entries.length ? <Mui.Typography sx={designType.helperText}>No salary history yet.</Mui.Typography> : null}
      <RequestInfoBanner body="One timeline from joining: proposed, upcoming, current and past. Each version shows the template it came from and how it was approved. Tick any two to compare." />
    </Mui.Stack>
  );
};

// ---------------- Compare ----------------

const CompareView = ({ employee, compare, olderLabel, newerLabel, onBack }) => {
  const diffCell = (row) => {
    if (row.change === "Added") return <Mui.Box component="span" sx={{ color: "#067647", fontWeight: designWeights.semibold }}>Added</Mui.Box>;
    if (row.change === "Removed") return <Mui.Box component="span" sx={{ color: "#B42318", fontWeight: designWeights.semibold }}>Removed</Mui.Box>;
    const diff = Math.round(row.diff || 0);
    if (!diff) return <Mui.Box component="span" sx={{ color: designColors.slate500 }}>—</Mui.Box>;
    return <Mui.Box component="span" sx={{ color: diff > 0 ? "#067647" : "#B42318", fontWeight: designWeights.semibold }}>{signed(diff)}</Mui.Box>;
  };
  const head = (label) => (
    <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
      {[label, olderLabel, newerLabel, "Difference"].map((cell, index) => (
        <Mui.TableCell key={`${cell}-${index}`} align={index ? "right" : "left"} sx={{ ...designShell.tableHeadCellBase, py: 1 }}>{cell}</Mui.TableCell>
      ))}
    </Mui.TableRow>
  );
  const cell = { fontSize: 14, color: designColors.slate900, borderBottom: `1px solid ${designColors.slate200}` };
  const row = (item, index) => (
    <Mui.TableRow key={`${item.code}-${index}`}>
      <Mui.TableCell sx={cell}>
        <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold }}>{item.name || item.code}</Mui.Typography>
        {item.name && item.code && item.code !== item.name ? <Mui.Typography sx={designType.captionText}>{item.code}</Mui.Typography> : null}
      </Mui.TableCell>
      <Mui.TableCell align="right" sx={cell}>{money(item.a)}</Mui.TableCell>
      <Mui.TableCell align="right" sx={cell}>{money(item.b)}</Mui.TableCell>
      <Mui.TableCell align="right" sx={cell}>{diffCell(item)}</Mui.TableCell>
    </Mui.TableRow>
  );
  const total = (label, values, withPct) => (
    <Mui.TableRow sx={{ backgroundColor: designColors.slateBg, "& td": { borderBottom: "none" } }}>
      <Mui.TableCell sx={{ ...cell, fontWeight: designWeights.heading }}>{label}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...cell, fontWeight: designWeights.heading }}>{money(values.a)}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...cell, fontWeight: designWeights.heading }}>{money(values.b)}</Mui.TableCell>
      <Mui.TableCell align="right" sx={{ ...cell, fontWeight: designWeights.heading, color: (values.diff || 0) >= 0 ? "#067647" : "#B42318" }}>
        {values.diff == null ? "—" : `${signed(values.diff)}${withPct && values.a ? ` · ${pct(values.a, values.b)}` : ""}`}
      </Mui.TableCell>
    </Mui.TableRow>
  );
  const card = (children) => (
    <Mui.Box sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, overflowX: "auto", backgroundColor: designColors.white }}>
      <Mui.Table size="small" sx={{ minWidth: 520 }}><Mui.TableBody>{children}</Mui.TableBody></Mui.Table>
    </Mui.Box>
  );
  const { left, right } = compare;
  return (
    <Mui.Stack spacing={2}>
      <Mui.Stack direction="row" spacing={1.5} alignItems="center">
        <Mui.Link component="button" type="button" underline="hover" onClick={onBack} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 14, fontWeight: designWeights.semibold, color: designColors.tealDark }}>
          <ArrowBack sx={{ fontSize: 16 }} /> Back to history
        </Mui.Link>
        <Mui.Typography sx={designType.captionText}>Comparing {olderLabel} and {newerLabel}</Mui.Typography>
      </Mui.Stack>
      {card(
        <>
          <Mui.TableRow sx={{ backgroundColor: designColors.slateBg }}>
            {["", `${olderLabel}${left.from ? ` · ${formatDay(left.from)}` : ""}`, `${newerLabel}${right.from ? ` · ${formatDay(right.from)}` : ""}`, "Difference"].map((labelText, index) => (
              <Mui.TableCell key={`${labelText}-${index}`} align={index ? "right" : "left"} sx={{ ...designShell.tableHeadCellBase, py: 1 }}>{labelText}</Mui.TableCell>
            ))}
          </Mui.TableRow>
          <Mui.TableRow>
            <Mui.TableCell sx={cell}>Template</Mui.TableCell>
            <Mui.TableCell align="right" sx={cell}>{left.template || "–"}</Mui.TableCell>
            <Mui.TableCell align="right" sx={cell}>{right.template || "–"}</Mui.TableCell>
            <Mui.TableCell align="right" sx={cell}>{compare.template_changed ? <ToneChip label="Changed" tone="blue" /> : "—"}</Mui.TableCell>
          </Mui.TableRow>
          <Mui.TableRow>
            <Mui.TableCell sx={cell}>Reason</Mui.TableCell>
            <Mui.TableCell align="right" sx={cell}>{left.reason || "–"}</Mui.TableCell>
            <Mui.TableCell align="right" sx={cell}>{right.reason || "–"}</Mui.TableCell>
            <Mui.TableCell sx={cell} />
          </Mui.TableRow>
          {total(`GROSS / ${periodOf(employee.pay_basis)}`, compare.gross, true)}
        </>,
      )}
      {card(<>{head("Earnings")}{compare.earnings.map(row)}{total("GROSS", compare.gross)}</>)}
      {card(<>{head("Deductions")}{compare.deductions.map(row)}{total("NET before statutory", compare.net, true)}</>)}
    </Mui.Stack>
  );
};

const exportComparison = (employee, compare, olderLabel, newerLabel) => {
  const value = (number) => (number == null ? "" : Math.round(number));
  const rows = [
    ["Template", compare.left.template, compare.right.template, compare.template_changed ? "Changed" : ""],
    ["Reason", compare.left.reason, compare.right.reason, ""],
    ...[...compare.earnings, ...compare.deductions].map((item) => [item.name || item.code, value(item.a), value(item.b), item.change || value(item.diff)]),
    ["GROSS", value(compare.gross.a), value(compare.gross.b), value(compare.gross.diff)],
    ["NET before statutory", value(compare.net.a), value(compare.net.b), value(compare.net.diff)],
  ];
  downloadCsv(`${employee.code || employee.id}_${olderLabel}_vs_${newerLabel}.csv`, ["Field", olderLabel, newerLabel, "Difference"], rows);
};

// ---------------- Panel ----------------

// details / history: { loading, data, error } from the single and history
// APIs; onCompare(a, b) returns the employee-salary-compare answer.
export const EmployeeSalaryPanel = ({ open, employee, details, history, canRevise, expanded, onToggleExpand, onClose, onCompare }) => {
  const routerHistory = useHistory();
  const trail = useSalaryPanelTrail(SCREEN, onClose);
  const [tab, setTab] = useState("details");
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState({ open: false, loading: false, data: null, error: "" });
  const id = employee?.id;
  const entries = history.data || [];
  useEffect(() => {
    setTab("details");
    setCompare({ open: false, loading: false, data: null, error: "" });
  }, [id]);
  // The latest two versions start ticked.
  useEffect(() => {
    setSelected(entries.filter(comparable).slice(0, 2).map((entry) => entry.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, history.data]);
  if (!employee) return null;

  const info = details.data ? { ...employee, ...details.data } : employee;
  const status = employeeStatus(info);
  const period = periodOf(info.pay_basis);
  const hasOpen = Boolean(info.has_open_revision || details.data?.pending.some((item) => item.kind === "Proposed"));
  const picked = entries.filter((entry) => selected.includes(entry.id));
  const [newer, older] = picked;
  const olderLabel = older ? entryLabel(older) : "";
  const newerLabel = newer ? entryLabel(newer) : "";
  const viewRevision = (number) => routerHistory.push(`${SALARY_REVISION_PATH}?review=${encodeURIComponent(number)}`);
  const revise = () => routerHistory.push(`${SALARY_REVISION_PATH}?revise=${encodeURIComponent(employee.id)}`);
  const runCompare = () => {
    if (!older || !newer) return;
    setCompare({ open: true, loading: true, data: null, error: "" });
    onCompare(older.id, newer.id)
      .then((data) => setCompare({ open: true, loading: false, data, error: "" }))
      .catch((error) => setCompare({ open: true, loading: false, data: null, error: error.message || "Couldn't compare these versions." }));
  };
  const closeCompare = () => setCompare({ open: false, loading: false, data: null, error: "" });
  const salaryVersion = details.data?.record.version;

  let footer;
  if (tab === "details") {
    footer = (
      <PanelFooter note={`Read-only${salaryVersion ? ` · salary version ${versionLabel(salaryVersion)}` : ""}`}>
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        {canRevise ? (
          <Mui.Tooltip title={hasOpen ? "This employee already has an open revision" : ""} arrow>
            <span><PrimaryButton onClick={revise} disabled={hasOpen}>Revise salary</PrimaryButton></span>
          </Mui.Tooltip>
        ) : null}
      </PanelFooter>
    );
  } else {
    footer = (
      <PanelFooter note={`Read-only${salaryVersion ? ` · salary version ${versionLabel(salaryVersion)}` : ""}`}>
        {compare.open && compare.data ? <SecondaryButton onClick={() => exportComparison(info, compare.data, olderLabel, newerLabel)}>Export comparison</SecondaryButton> : null}
        <SecondaryButton onClick={onClose}>Close</SecondaryButton>
      </PanelFooter>
    );
  }

  let body;
  if (tab === "details") {
    body = details.loading && !details.data ? loadingText("Loading the salary details...") : details.error ? errorText(details.error) : details.data ? <DetailsTab employee={info} details={details.data} onViewRevision={viewRevision} /> : null;
  } else if (compare.open) {
    body = compare.loading ? (
      loadingText("Comparing...")
    ) : compare.error ? (
      <Mui.Stack spacing={1.5}>
        {errorText(compare.error)}
        <Mui.Link component="button" type="button" underline="hover" onClick={closeCompare} sx={{ alignSelf: "flex-start", fontSize: 14, fontWeight: designWeights.semibold, color: designColors.tealDark }}>Back to history</Mui.Link>
      </Mui.Stack>
    ) : compare.data ? (
      <CompareView employee={info} compare={compare.data} olderLabel={olderLabel} newerLabel={newerLabel} onBack={closeCompare} />
    ) : null;
  } else {
    body = history.loading && !history.data ? loadingText("Loading the salary history...") : history.error ? errorText(history.error) : <HistoryTab employee={info} entries={entries} selected={selected} setSelected={setSelected} onCompare={runCompare} />;
  }

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      breadcrumb={tab !== "details" && compare.open ? [...trail, { label: info.name, onClick: closeCompare }, "Compare versions"] : [...trail, info.name]}
      header={
        <PanelTitleBlock
          icon={<Mui.Typography sx={{ fontWeight: designWeights.heading, fontSize: 16 }}>{initials(info.name)}</Mui.Typography>}
          title={info.name}
          chips={<ToneChip label={status.label} tone={status.tone} dot />}
          subtitle={[info.code, info.department, info.position, info.group, info.pay_basis ? `${info.pay_basis} pay cycle` : null].filter(Boolean).join(" · ")}
        />
      }
      bodySx={panelBodySx}
      footer={footer}
    >
      <Mui.Stack spacing={2}>
        <SummaryStrip
          items={[
            { label: `Gross / ${period}`, value: money(info.gross) },
            { label: "Template", value: templateText(info.template_name, info.template_version) },
            { label: "Structure", value: info.customized ? "Customized" : "Template Linked" },
            { label: "Effective from", value: formatDay(info.effective_from) },
          ]}
        />
        <CountTabs
          value={tab}
          onChange={(next) => { setTab(next); closeCompare(); }}
          tabs={[
            { value: "details", label: "Salary Details" },
            { value: "history", label: "Salary History", count: history.data ? entries.length : undefined },
          ]}
          sx={{ backgroundColor: "transparent" }}
        />
        {body}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
